import React, { useEffect, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { Place } from '@mui/icons-material';

// Photon (OpenStreetMap) geocoder: free, no API key, built for search-as-you-type.
const PHOTON_URL = 'https://photon.komoot.io/api/';
const LOCALITY_TAGS = ['place:city', 'place:town', 'place:village'];

// Region codes that Intl knows but are not current countries (groupings and deprecated aliases).
const NON_COUNTRIES = new Set([
  'AC', 'CP', 'CQ', 'DG', 'EA', 'EU', 'EZ', 'IC', 'QO', 'TA', 'UN', 'ZZ',
  'AN', 'BU', 'CS', 'DD', 'DY', 'FX', 'HV', 'NH', 'RH', 'SU', 'TP', 'UK', 'VD', 'YD', 'YU', 'ZR',
]);

export const COUNTRIES = (() => {
  const names = new Intl.DisplayNames(['es'], { type: 'region', fallback: 'none' });
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const list = [];
  for (const a of letters) {
    for (const b of letters) {
      const code = a + b;
      let name;
      try {
        name = names.of(code);
      } catch {
        name = undefined;
      }
      // X* codes are private-use pseudo-regions, except Kosovo (XK).
      const isPrivateUse = code.startsWith('X') && code !== 'XK';
      if (name && name !== code && !NON_COUNTRIES.has(code) && !isPrivateUse) {
        list.push({ code, name });
      }
    }
  }
  return list.sort((x, y) => x.name.localeCompare(y.name, 'es'));
})();

const unique = (names) => [...new Set(names.filter(Boolean))];

const normalize = (text = '') =>
  text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

// country-state-city names some subdivisions in English ("Artigas Department").
const STATE_SUFFIX = / (Department|Province|Region|District|Governorate|Prefecture|County|Municipality|Parish)$/;

/**
 * Provinces of a country as [{ name, lat, lon }], sorted. The ~550 KB dataset is
 * code-split and only downloaded the first time a country is picked.
 */
export const loadStates = async (countryCode) => {
  const { getStatesOfCountry } = await import('country-state-city/lib/state');
  const states = getStatesOfCountry(countryCode).map((s) => ({
    name: s.name.replace(STATE_SUFFIX, ''),
    lat: s.latitude,
    lon: s.longitude,
  }));
  return states.sort((x, y) => x.name.localeCompare(y.name, 'es'));
};

/** Localities matching `query`, preferring those in `state` ({ name, lat, lon } or a typed name). */
export const searchLocalities = async (query, countryCode, state, signal) => {
  const params = new URLSearchParams({ q: query, limit: '20', lang: 'default' });
  LOCALITY_TAGS.forEach((tag) => params.append('osm_tag', tag));
  params.append('countrycode', countryCode);
  if (state.lat && state.lon) {
    params.append('lat', state.lat);
    params.append('lon', state.lon);
  }

  const res = await fetch(`${PHOTON_URL}?${params}`, { signal });
  if (!res.ok) throw new Error(`Photon ${res.status}`);
  const places = (await res.json()).features.map((f) => f.properties);

  // OSM and the provinces dataset don't always spell the province the same way.
  const wanted = normalize(state.name);
  const inState = places.filter((p) => {
    const osmState = normalize(p.state);
    return osmState && (osmState.includes(wanted) || wanted.includes(osmState));
  });
  return unique((inState.length ? inState : places).map((p) => p.name)).slice(0, 8);
};

// Chrome ignores autocomplete="off" on address-like fields but honours "new-password";
// the data attributes keep LastPass and 1Password out as well.
export const NO_AUTOFILL = { autoComplete: 'new-password', 'data-lpignore': 'true', 'data-1p-ignore': 'true' };

export const paperSx = {
  mt: 0.5,
  borderRadius: '10px',
  bgcolor: '#111a1b',
  border: '1px solid rgba(94, 234, 212, 0.12)',
  boxShadow: '0 16px 40px rgba(0, 0, 0, 0.6)',
  color: 'rgba(235, 255, 244, 0.85)',
  fontSize: '0.82rem',
  '& .MuiAutocomplete-option': {
    gap: 1,
    '&.Mui-focused': { bgcolor: 'rgba(45, 212, 191, 0.1)' },
    '&[aria-selected="true"]': { bgcolor: 'rgba(45, 212, 191, 0.18)' },
  },
  '& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading': {
    color: 'rgba(235, 255, 244, 0.5)',
    fontSize: '0.8rem',
  },
};

/**
 * Styled MUI Autocomplete for places.
 * Pass `options` for a fixed list (filtered locally), or `search(query, signal)` to fetch
 * suggestions as the user types. `freeSolo` (default: on for `search`) also accepts typed text.
 */
const PlaceAutocomplete = ({
  value,
  onChange,
  options: staticOptions,
  getOptionLabel,
  search,
  freeSolo = Boolean(search),
  loading: externalLoading = false,
  disabled,
  placeholder,
  label,
  inputSx,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [remoteOptions, setRemoteOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // With freeSolo MUI keeps the typed text when the value is cleared from outside
  // (e.g. picking another country), so clear it here.
  useEffect(() => {
    if (!value) setInputValue('');
  }, [value]);

  useEffect(() => {
    if (!search) return undefined;

    const query = inputValue.trim();
    if (query.length < 2 || query === value) {
      setRemoteOptions(value ? [value] : []);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        setRemoteOptions(await search(query, controller.signal));
      } catch (error) {
        if (error.name !== 'AbortError') setRemoteOptions([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [inputValue, value, search]);

  // With freeSolo the value can be typed text even when options are objects.
  const labelOf = (option) =>
    typeof option === 'string' || !getOptionLabel ? option : getOptionLabel(option);

  return (
    <Autocomplete
      fullWidth
      value={value || null}
      onChange={(_, newValue) => onChange(newValue || null)}
      inputValue={inputValue}
      onInputChange={(_, newInput) => setInputValue(newInput)}
      options={search ? remoteOptions : staticOptions}
      getOptionLabel={labelOf}
      isOptionEqualToValue={(option, selected) => labelOf(option) === labelOf(selected)}
      filterOptions={search ? (x) => x : undefined}
      freeSolo={freeSolo}
      autoSelect={freeSolo}
      loading={loading || externalLoading}
      disabled={disabled}
      autoHighlight
      loadingText="Buscando..."
      noOptionsText="Sin resultados"
      slotProps={{ paper: { sx: paperSx } }}
      renderOption={(props, option) => (
        <li {...props} key={labelOf(option)}>
          <Place sx={{ fontSize: 16, color: '#2dd4bf' }} />
          {labelOf(option)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          margin={label ? 'dense' : undefined}
          placeholder={placeholder}
          inputProps={{ ...params.inputProps, 'aria-label': label || placeholder, ...NO_AUTOFILL }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(loading || externalLoading) && <CircularProgress size={14} sx={{ color: '#2dd4bf' }} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          // Compact custom look when given inputSx (login card); standard MUI field otherwise.
          sx={{
            '& .MuiOutlinedInput-root': inputSx
              ? {
                  ...inputSx,
                  py: 0,
                  pl: '14px',
                  '& .MuiAutocomplete-input': { p: 0 },
                  '&.Mui-disabled': { opacity: 0.5 },
                }
              : {},
            '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
              color: 'rgba(235, 255, 244, 0.45)',
            },
          }}
        />
      )}
    />
  );
};

export default PlaceAutocomplete;
